<template>
<div class="company-form-section-container"
	v-touch:swipe.left="swipeLeft" 
	v-touch:swipe.right="swipeRight">

	<div class="section-content-container">

		<component :is="isChildTemplate ? 'div' : 'transition'" :name="transitionName"
			v-on:after-enter="transitionDone"
			v-on:after-leave="transitionDone">

			<section v-for="(section,sectionIndex) in localFormSections" 
				v-if="sectionIndex == currentSectionIndex || isChildTemplate"
				:key="sectionIndex"
				class="form-section">
				
				<template v-if="currentSectionIndex < localFormSections.length - 1 || isChildTemplate">
					<h4>{{ section.sectionHeading }}</h4>

					<form-fillable-fields v-model="section.fields"
						:tasks="tasks" 
						:hazards="hazards" 
						:controls="controls"
						:readonly="readonly"
						:project="project"
						:service="service"
						:section-index="sectionIndex"
						:is-child-template="isChildTemplate"
						v-on:fetchHazards="fetchHazards"
						v-on:fetchControls="fetchControls">
					</form-fillable-fields>

					<div class="alert alert-danger mt-2 text-center" v-if="wrongAnswerFields.length > 0" style="font-size: 16pt;">
						<div><i class="fa fa-warning"></i></div>
						You have provided answers that indicate you are not ready to proceed. Please contact your supervisor immediately.
					</div>

					<div v-if="!isChildTemplate" class="d-flex flex-row justify-content-between mt-3">
						<div>
							<button v-if="currentSectionIndex > 0" class="btn btn-light" type="button" v-on:click="prevSection">
								<i class="fa fa-chevron-left"></i>
								Back
							</button>
						</div>

						<div>
							<button type="button" class="btn btn-primary" v-on:click="nextSection">
								I understand: Next

								<i class="fa fa-chevron-right"></i>
							</button>
						</div>
					</div>	
				</template>


				<!---------------------------- Form Acknowledgement Page --------------------------------->

				<div v-else-if="!isChildTemplate">

					<div class="bg-light p-3 mb-2" v-if="formIsActive">
						<h3>Acknowledgement</h3>

						<template v-if="'id' in $store.userDetails || 'acknowledgement_slug' in $route.params">
							<p>
								I<template v-if="userDetails.name">, {{userDetails.name}},</template> certify that I have reviewed this form and it is correct to the best of my knowledge:
							</p>
						</template>
						<template v-else>
							<field label="Name"
								col-class="col-auto"
								v-model="userDetails.name">
							</field>

							<field label="Email"
								col-class="col-auto"
								v-model="userDetails.email">
							</field>

							<field label="Phone"
								col-class="col-auto"
								v-model="userDetails.phone">
							</field>

							<field label="Company Name"
								col-class="col-auto"
								v-model="userDetails.company_name">
							</field>

						</template>

						<div class="d-flex flex-wrap">

							<signature-field
								class="ml-3 mr-4"
								:external-signer-name="userDetails.name"
								:readonly="isSignatureReadonly"
								v-model="userSignature"
								v-on:input="addUserToParticipants">
							</signature-field>


							<!-------- "Form Complete" checkmark -------->

							<div class="mt-2">
								<div v-if="uniqueCompiledWarnings.length > 0">

									<div class="d-flex">
										<i class="fa fa-2x fa-exclamation-circle text-warning"></i>
										<h2 class="text-warning mb-1">Form Complete, with Warnings</h2>
									</div>

									<alert v-for="(warning,index) in uniqueCompiledWarnings"
											 :key="index"
											 classes="alert-warning"
											 icon="fa-exclamation"
											 :self-clear="false"
											 :unclosable="true"
											 :visible="true"
											 :message="warning">
									</alert>

								</div>
								<div v-else-if="isUserSignatureSet">

									<div class="d-flex">
										<i class="fa fa-2x fa-check-circle mr-2" style="color: green"></i>
										<h2 class="mb-1" style="color: green">Form Complete!</h2>
									</div>

									<span v-if="formIsActive">Make sure to add any participants and click the <strong>Submit</strong> button below</span>

								</div>
							</div>

						</div>


					</div>


					<!-------- Participants Section -------->

					<div class="bg-light p-3">

						<h3>Participants</h3>

						<p v-if="!noParticipantControls">All participants should sign below:</p>

						<table-list record-name="Participant"
							:fields="participantFields"
							:records="mutatedParticipants"
							:sortable="true"
							:has-delete="!noParticipantControls"
							delete-endpoint="CompanyFormAcknowledgement/delete"
							id-field="slug"
							v-on:delete="removeParticipant">

							<template v-if="!noParticipantControls">
								<button class="btn btn-primary mr-2" type="button" v-on:click="addExternalParticipant">
									<i class="fa fa-plus"></i>
									Add External Participant
								</button>

								<button class="btn btn-primary" type="button" v-on:click="inviteParticipant">
									<i class="fa fa-plus"></i>
									Invite Participant
								</button>
							</template>

						</table-list>
					</div>


					<!-------- Errors with form (if they exist) -------->

					<alert v-if="showFormError" 
						:visible="showFormError" 
						classes="alert-danger" 
						:message="formErrorMsg"
						v-on:close="showFormError = false">		
					</alert>


					<!-------- Navigation/Submission Bar -------->

					<div class="d-flex flex-row justify-content-between mt-3">
						<div>
							<button class="btn btn-light" type="button" v-on:click="prevSection">
								<i class="fa fa-chevron-left"></i>
								Back
							</button>
						</div>

						<div>

							<button v-if="formIsActive" class="btn btn-success btn-lg" v-on:click="submitForm" :disabled="working">
								<i class="fa fa-check-square mr-2"></i>
								Submit Form
							</button>
							
						</div>
					</div>	
				</div>

			</section>
		</component>

	</div>

	<modal-dialog :modal-visible="participantModal.visible"
		:title="participantModalTitle"
		confirm-button-text="Confirm"
		v-on:closeModal="participantModal.visible = false"
		v-on:confirm="confirmAddParticipant">

		<field v-if="participantModal.mode == 'invite'"
			label="Who is the participant?"
			col-class="col-auto"
			:params="{
				type:'btn-group',
				buttons: [
					{text: 'User',value:false},
					{text: 'External Worker',value:true}
				]
			}"
			v-model="participantModal.payload.is_external_worker">
		</field>

		<template v-if="participantModal.payload.is_external_worker">

			<p v-if="participantModal.mode == 'invite'">This will send an SMS message or email to the individual with a link to sign the form.</p>

			<field label="Name"
				col-class="col-md-4"
				key="name"
				v-model="participantModal.payload.name">
			</field>

			<field label="Company Name"
				col-class="col-md-4"
				v-model="participantModal.payload.company_name">
			</field>

			<field label="Phone"
				col-class="col-md-4"
				v-model="participantModal.payload.phone"
				:params="{type:'tel'}">
			</field>

			<field label="Email"
				col-class="col-md-4"
				v-model="participantModal.payload.email"
				:params="{type:'email'}">
			</field>
		</template>
		<template v-else-if="participantModal.mode == 'invite'">

			<p>This will send a push notification to the users you select with a link to sign the form.</p>

			<field label="Users"
				key="users"
				col-class="col-md-4"
				:params="{type: 'select-multi',options: userOptions(true)}"
				v-model="participantModal.userIdsInvite">
			</field>

		</template>

		<form-group v-if="participantModal.mode == 'add'" label="Signature"
			col-class="col-md-4">

			<signature-field :external-signer-name="participantModal.payload.name" 
				v-model="participantModal.signature"></signature-field>

		</form-group>

	</modal-dialog>

</div>
</template>
<script type="text/javascript">
import Field from "@/components/Field"
import FormGroup from "@/components/FormGroup"
import TableList from "@/components/TableList"
import ModalDialog from "@/components/ModalDialog"
import Alert from "@/components/Alert"

import SignatureField from "@/components/SignatureField"

import UploadFieldWithPanel from "@/components/super_components/UploadFieldWithPanel"

import WeatherWidget from "@/components/super_components/WeatherWidget"

import FormFillableFields from "@/components/company_forms/FormFillableFields"

import FormMixins from "@/mixins/FormMixins"
import draggable from 'vuedraggable'

export default {
	name: "FormFillable",
	components: {
		Alert,
		Field,
		FormGroup,
		WeatherWidget,
		UploadFieldWithPanel,
		draggable,
		FormFillableFields,
		TableList,
		ModalDialog,
		SignatureField
	},
	mixins: [FormMixins],
	props: ["value", "isChildTemplate","acknowledgements","readonly","noParticipantControls","project","service","formIsActive","skipForm"],
	data() {
		return {
			editorVisible: false,
			template_id: null,
			id: null,
			localFormSections: [],
			addCustomTask: false,
			currentSectionIndex: 0,
			tasks: [],
			hazards: [],
			controls: [],
			hazardsFetched: false,
			controlsFetched: false,
			compiledWarnings: new Set(),
			missingFields: [],
			wrongAnswerFields: [],
			showFormError: false,
			formErrorMsg: "",
			userDetails: {
				name: "",
				email: "",
				phone: "",
				company_name: ""
			},
			userSignature: {
				name: "",
				trail: [],
				dataUrl: ""
			},
			transitionName: "slide-left",
			participantModal: {
				visible: false,
				working: false,
				signature: {},
				mode: "add",
				userIdsInvite: [],
				payload: {
					is_external_worker: false
				}
			},
			working: false,
			isSignatureReadonly: false
		}
	},
	watch: {
		value() {
			this.localFormSections = this.initLocalFormValues(this.value)
		},
		acknowledgements() {
			this.setParticipants()
		},
		skipForm() {
			this.currentSectionIndex = (this.skipForm) ? this.localFormSections.length - 1 : 0
		}
	},
	computed: {
		isUserSignatureSet() {
			try {
				let signatureObj = JSON.parse(this.userSignature)
				return (("sigSkipped" in signatureObj && signatureObj.sigSkipped) || ('trail' in signatureObj && signatureObj.trail && signatureObj.trail.length > 0))
			} catch (e) {}

			return false
		},
		taskOptions() {
			return this.optionify(this.tasks,"description",true)
		},
		uniqueCompiledWarnings() {
			return [...this.compiledWarnings]
		},
		participantModalTitle() {
			if (this.participantModal.mode == 'invite')
				return "Invite Participant"
			else
				return "Add Participant"
		}
	},
	methods: {
		setParticipants() {
			this.participants = this.acknowledgements

			if (this.acknowledgements) {

				let myAck = this.participants.find(p => {
					return ('user_id' in p && p.user_id == this.$store.userDetails.id)
				})

				// This user has already submitted this form
				if (myAck) {
					try {
						this.userSignature = JSON.parse(myAck.signature_json)
					}
					catch (e) {}

					//this.isSignatureReadonly = true
				} else {
					this.userSignature = {
						name: "",
						trail: [],
						dataUrl: ""
					}
					this.isSignatureReadonly = false
				}
			}
		},
		setUserDetails() {
			if ("acknowledgement_slug" in this.$route.params) {
				let myAck = this.acknowledgements.find(ack => 'slug' in ack && ack.slug == this.$route.params.acknowledgement_slug)

				if (myAck) {
					this.userDetails.name = myAck.name
					this.userDetails.phone = myAck.phone
					this.userDetails.email = myAck.email
					this.userDetails.company_name = myAck.company_name
				}
			} else if ("id" in this.$store.userDetails) {
				this.userDetails.id = this.$store.userDetails.id
				this.userDetails.name = this.$store.userDetails.first_name+" "+this.$store.userDetails.last_name
				this.userDetails.phone = this.$store.userDetails.phone_mobile
				this.userDetails.email = this.$store.userDetails.email
				this.userDetails.company_name = this.$store.company.company_name
			} else {
				this.userDetails.name = ""
				this.userDetails.phone = ""
				this.userDetails.email = ""
				this.userDetails.company_name = ""
			}
		},
		initLocalFormValues(template) {
			let vm = this

			let sections = []

			if (template) {

				sections = template.map(section => {

					return {
						sectionHeading: section.sectionHeading,
						fields: section.fields.map(field => {

							if (field.type == "hazard-list" || field.type == "control-list" || field.type == "select-multi" || field.type == "checklist") {
								if (!("value" in field) || typeof field.value != "object")
									field.value = []
							}
							else if (field.type == 'tasklist') {
								if (!("value" in field) || typeof field.value != "object")
									field.value = []
							}
							else if ("default" in field && !("value" in field) || field.value == null) {
								//$. indicates dynamic value

								if (field.default && typeof field.default == 'string' && field.default.substring(0,2) == "$.") {
									//is a dynamic value, split by periods
									let segs = field.default.substring(2).split(".")

									if (segs.length == 1)
										field.value = vm.$store[segs[0]]
									else if (segs.length == 2)
										field.value = vm.$store[segs[0]][segs[1]]
									else if (segs.length == 3)
										field.value = vm.$store[segs[0]][segs[1]][segs[2]]
									else if (segs.length == 4)
										field.value = vm.$store[segs[0]][segs[1]][segs[2]][segs[3]]
								}
								else
									field.value = field.default //not a dynamic value, just prefill with default
							}

							// Legacy fix: field.conditionalImageResponseValue is now a LIST of images, instead of just one
							if ('conditionalImageResponseValue' in field && field.conditionalImageResponseValue) {

								if (!Array.isArray(field.conditionalImageResponseValue))
									field.conditionalImageResponseValue = [field.conditionalImageResponseValue]
							}

							return field
						})
					}
				})
			}

			//push final empty section so last complete section will show
			if (!this.isChildTemplate && !sections.find(section => "isCompleteSection" in section && section.isCompleteSection))
				sections.push({sectionHeading: "Complete!",fields:[],isCompleteSection:true})

			return sections
		},
		fetchHazards() {
			let vm = this

			if (!this.hazardsFetched) {
				this.getJSON("SafetyHazard/get").then(response => {
					vm.hazards = response.data
					vm.hazardsFetched = true
				})
			}
		},
		fetchControls() {
			let vm = this

			if (!this.controlsFetched) {
				this.getJSON("SafetyControl/get").then(response => {
					vm.controls = response.data
					vm.controlsFetched = true
				})
			}
		},
		swipeLeft() {
			if (!this.participantModal.visible && !this.$store.isUserDrawing)
				this.nextSection()
		},
		swipeRight() {
			if (!this.participantModal.visible && !this.$store.isUserDrawing)
				this.prevSection()
		},
		prevSection() {
			this.transitionName = "slide-right"

			if (this.currentSectionIndex > 0) {

				this.setContainerHeightForTransition()

				this.missingFields = []
				this.currentSectionIndex--
			}
		},
		nextSection() {
			//make sure all required responses filled out

			this.transitionName = "slide-left"

			let vm = this

			let missingFields = []
			let wrongAnswerFields = []

			this.localFormSections[this.currentSectionIndex].fields.forEach((field,fieldIndex) => {
				let key = "field_"+this.currentSectionIndex+"_"+fieldIndex

				let deferredFieldTypes = [
					"personnel-phone-list",
					"weather-widget",
					'static-paragraph'
				]

				let fieldIsRequired = false
				if ("requiredField" in field)
					fieldIsRequired = field.requiredField
				else if ("required" in field)
					fieldIsRequired = field.required

				let isEmptyArray = false
				if (Array.isArray(field.value)) {
					// Multi-select can sometimes produce [""] / [null] when "nothing" is selected.
					let meaningfulSelections = field.value.filter(val => val !== null && val !== undefined && val !== "")
					isEmptyArray = meaningfulSelections.length == 0
				}

				let isMissingValue = (!("value" in field) || field.value === null || field.value === undefined || isEmptyArray || field.value === "")

				if (deferredFieldTypes.indexOf(field.type) == -1 && isMissingValue && fieldIsRequired) {
					missingFields.push(fieldIndex)
					vm.$set(vm.localFormSections[this.currentSectionIndex].fields[fieldIndex],"hasError",true)
				}
				//show warning message for positive answer
				else if (deferredFieldTypes.indexOf(field.type) == -1) {

					vm.$set(vm.localFormSections[this.currentSectionIndex].fields[fieldIndex],"hasError",false)

					if (field.value && field.positiveWarningMessage) 
						vm.compiledWarnings.add(field.positiveWarningMessage)
					
					//show warning message for negative answer
					else if (!field.value && field.negativeWarningMessage)
						vm.compiledWarnings.add(field.negativeWarningMessage)

					if ("positiveResponseRequired" in field && field.positiveResponseRequired && !field.value)
						wrongAnswerFields.push(fieldIndex)
					else if ("negativeResponseRequired" in field && field.negativeResponseRequired && field.value)
						wrongAnswerFields.push(fieldIndex)
				}

			})

			if (missingFields.length > 0) 
				vm.missingFields = missingFields
			
			vm.wrongAnswerFields = wrongAnswerFields

			if (missingFields.length == 0 && wrongAnswerFields.length == 0) {
				if (this.currentSectionIndex < this.localFormSections.length - 1) {

					this.setContainerHeightForTransition()

					vm.missingFields = []
					this.currentSectionIndex++
				}
			}
		},
		emitForm() {
			this.$emit("input",this.localFormSections)
		},
		addUserToParticipants() {

			let userParticipant = this.participants.find(p => {
				return ('acknowledgement_slug' in this.$route.params && p.slug == this.$route.params.acknowledgement_slug) ||
					('user_id' in p && p.user_id == this.$store.userDetails.id)
			})

			// Update user's signature or push it
			if (userParticipant) {
				userParticipant.signature_json = JSON.stringify(this.userSignature)

				//remove id, because any record with an id will be ignored as a previous signature, but we want to re-sign
				delete userParticipant.id
			} else {
				this.participantModal.signature = this.cloneObj(this.userSignature)
				this.participantModal.mode = 'add'
				this.participantModal.payload = {
					is_external_worker: false
				}
				this.setUserDetails()
				this.confirmAddParticipant()
			}
		},
		addExternalParticipant() {
			this.participantModal.visible = true
			this.participantModal.signature = null
			this.participantModal.mode = "add"
			this.participantModal.payload = {
				is_external_worker: true
			}
		},
		confirmAddParticipant() {
			let vm = this

			if (this.participantModal.mode == 'add') {
				let payload = this.cloneObj(this.participantModal.payload)

				payload.signature_json = JSON.stringify(this.participantModal.signature)

				if (payload.is_external_worker) {
					payload.name = this.participantModal.payload.name
					payload.email = this.participantModal.payload.email
					payload.phone = this.participantModal.payload.phone
					payload.company_name = this.participantModal.payload.company_name
				}
				else {
					payload.user_id = this.$store.userDetails.id
					payload.name = this.userDetails.name
					payload.email = this.userDetails.email
					payload.phone = this.userDetails.phone
					payload.company_name = this.userDetails.company_name

					if (!("id" in this.userDetails))
						payload.is_external_worker = 1
				}

				this.participants.push(this.cloneObj(payload))

				//this.$emit("participantsChange",this.participants)
				//this.$emit("participantAdded",payload)
			}
			else {

				if (this.participantModal.payload.is_external_worker == 1) {

					// Require that phone or email is provided
					if (!this.participantModal.payload.email && !this.participantModal.payload.phone) {
						this.alertFail('Must provide a phone number or email address.', true)
						return
					}

					let payload = this.cloneObj(this.participantModal.payload)
					payload.is_invite = true
					this.participants.push(this.cloneObj(payload))

				}
				else {

					this.participantModal.userIdsInvite.forEach(user_id => {
						let user = vm.$store.users.find(theUser => theUser.id == user_id)

						if (user && !vm.participants.find(participant => participant.user_id == user_id)) {
							vm.participants.push({
								name: user.first_name+" "+user.last_name,
								email: user.email,
								phone: user.phone_mobile,
								company_name: vm.$store.company.company_name,
								user_id,
								is_invite: true
							})
						}
					})

					this.participantModal.userIdsInvite = []
				}
			}

			this.participantModal.visible = false
		},
		removeParticipant(record) {
			let index = this.participants.findIndex(part => part.name == record.name)

			if (index == -1)
				return

			let participant = this.participants[index]

			// If the participant is the user, also clear the signature
			if (participant.user_id == this.$store.userDetails.id) {
				this.userSignature = ""
				this.isSignatureReadonly = false
			}

			this.participants.splice(index,1)

			//this.$emit("participantsChange",this.participants)
			//this.$emit("participantRemoved",index)
		},
		inviteParticipant() {
			this.participantModal.visible = true
			this.participantModal.signature = null
			this.participantModal.mode = "invite"
			this.participantModal.payload = {
				is_external_worker: false
			}

			this.fetchUsers([],false,["id","slug","first_name","last_name","email","phone_mobile"])
		},
		submitForm() {

			/**
			this.postData("ErrorLog/save",{
				user_id: this.$store.userDetails.id,
				endpoint: "Front End",
				user_error_message: "'Submit Form' button clicked",
				payload_json: JSON.stringify(this.localFormSections),
				tenant_id: this.$store.userDetails.tenant_id,
				user_agent: navigator.userAgent
			})
			*/

			if (this.isUserSignatureSet) {
				this.working = true

				/**
				this.postData("ErrorLog/save",{
					user_id: this.$store.userDetails.id,
					endpoint: "Front End",
					user_error_message: "'Submit Form' event emitted",
					payload_json: JSON.stringify(this.localFormSections),
					tenant_id: this.$store.userDetails.tenant_id,
					user_agent: navigator.userAgent
				})
				*/

				// From each image field, remove the isNew and key markers if they are set
				// These fields are set in FormFillableFields
				this.localFormSections.forEach(section => {

					section.fields.forEach(field => {
						if ('type' in field && (field.type == 'image' || field.type == 'whiteboard')) {

							if ('value' in field && field.value) {
								field.value.forEach(img => {
									if ('meta' in img)
										delete img.meta
								})
							}

						}

						// Remember: conditionalImageResponseValue can be either a single object (legacy), or a list of objects (current)
						if ('conditionalImageResponseValue' in field && field.conditionalImageResponseValue) {

							field.conditionalImageResponseValue.forEach(img => {
								if ('meta' in img)
									delete img.meta
							})

						}
					})

				})

				this.$emit("submit",{
					participants: this.participants,
					formSections: this.localFormSections
				})
			}
			else {
				this.showFormError = true
				this.formErrorMsg = "You must sign the form to continue, or click 'Skip Signature'"
			}
		},
		setContainerHeightForTransition() {
			let container = document.querySelector(".company-form-section-container")
			container.style.height = container.clientHeight+"px"
			container.style.overflow = "hidden";
		},
		transitionDone() {
			let container = document.querySelector(".company-form-section-container")

			/*let contentContainer = document.querySelector('.company-form-section-container .content-container')

			if (contentContainer && contentContainer.scrollHeight > 0) {
				let height = contentContainer.scrollHeight+35

				container.style.height = height+"px"
			}
			else
				container.style.height = "auto"
			*/

			container.style.height = "auto"

			container.style.overflow = "auto";
		},
		fetchData() {
			let vm = this

			let promises = [
				this.getJSON("SafetyJobTask/get",{include:["SafetyHazard","SafetyControl"]}),
				this.fetchHazards(),
				this.fetchControls()
			]

			Promise.all(promises).then(responses => {
				vm.tasks = responses[0].data
			})
		},
	},
	created() {
		let vm = this
		
		this.setUserDetails()
		this.setParticipants()

		this.fetchData()
		this.localFormSections = this.initLocalFormValues(this.value)

		this.currentSectionIndex = (this.skipForm) ? this.localFormSections.length - 1 : 0

	}
}
</script>

<style scoped>
	.form-section {
		padding: 15px;
		background: white;
		border: 1px solid #e0e0e0;
	}

	.task-item {
		position: relative;
		border: 1px solid #e0e0e0;
		padding: 5px;
		padding-top: 35px;
		margin-bottom: 10px;
	}

	.task-item .icon-button {
		position: absolute;
		top: 5px;
		right: 5px;
	}

	.list-cell {
		position: relative;
		padding-bottom: 140px;
	}

	.add-to-list-select {
		width: calc(100% - 10px);
		position: absolute;
		bottom: 70px;
	}

	.add-to-list-text {
		width: calc(100% - 10px);
		position: absolute;
		bottom: 0;
	}

	.hazard-control-list-item {
		position: relative;
		cursor: pointer;
	}

	.hazard-control-list-item .fa-trash {
		position: absolute;
		right: 0;
		display: none;
	}

	.hazard-control-list-item:hover .fa-trash {
		display: inline;
	}

	/**
	* TRANSITION CLASSES
	*/

	.company-form-section-container {
		position: relative;
		transition: height 0.5s;
		overflow-x: hidden;
	}

	.slide-left-enter-active, .slide-left-leave-active {
		transition: all 0.5s;
		position: absolute;
		width: calc(100% - 2rem);
	}

	.slide-left-enter-active {
		opacity: 0;
		left: 500px;
	}

	.slide-left-enter-to {
		left: 1rem;
		opacity: 1;
	}

	.slide-left-leave-active {
		opacity: 1;
		left: 1rem;
	}

	.slide-left-leave-to {
		left: -500px;
		opacity: 0;
	}

	.slide-right-enter-active, .slide-right-leave-active {
		transition: all 0.5s;
		position: absolute;
		width: calc(100% - 2rem);
	}

	.slide-right-enter-active {
		opacity: 0;
		left: -500px;
	}

	.slide-right-enter-to {
		left: 1rem;
		opacity: 1;
	}

	.slide-right-leave-active {
		opacity: 1;
		left: 1rem;
	}

	.slide-right-leave-to {
		left: 500px;
		opacity: 0;
	}

	/*
	* END TRANSITION CLASSES
	*/

</style>